import type { CalendarEventView } from "@portal/schemas";
import { useEffect, useRef, useState } from "react";
import { Button, Popover, PopoverContent, PopoverTrigger } from "@portal/ui";
import type { ShownCalendar } from "@portal/schemas";
import {
  Building2,
  CircleX,
  ExternalLink,
  FileText,
  House,
  MapPin,
  Paperclip,
  Video,
} from "lucide-react";
import { siGooglemeet } from "simple-icons";
import type { CSSProperties } from "react";
import { calendarColor, readableOn } from "./calendar-color";
import {
  minutesFromDayStart,
  placeEvents,
  placeWorkingLocations,
  visibleHours,
  type PlacedEvent,
  type PlacedWorkingLocation,
} from "./day-layout";

/** 1 時間の高さ。予定の要素とボタンに余裕を持たせるため、広めにする。 */
const HOUR_PX = 56;
/** 予定のブロックの最小の高さ(30 分の予定でも、ボタン(24px)がクリックしやすい高さ)。 */
const MIN_BLOCK_PX = 28;
/** 勤務場所のバーと印の分、予定を右へずらす幅。 */
const WORKING_LOCATION_INSET = 20;
const TZ = "Asia/Tokyo";
const time = new Intl.DateTimeFormat("ja-JP", {
  hour: "numeric",
  minute: "2-digit",
  hour12: false,
  timeZone: TZ,
});
const dateLabel = new Intl.DateTimeFormat("ja-JP", {
  month: "long",
  day: "numeric",
  weekday: "short",
  timeZone: TZ,
});
/** 読み上げ用。「参加しない」と返事した予定に添える。 */
const declinedLabel = (e: CalendarEventView) => (e.declined ? "(参加しない)" : "");
const range = (e: CalendarEventView) =>
  `${time.format(new Date(e.start))}–${time.format(new Date(e.end))}`;

/** 現在時刻。1 分ごとに更新して、現在時刻の線を動かす。 */
function useNow(intervalMs = 60_000): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

/**
 * 今日の予定を、Google カレンダーの 1 日表示のように時間軸に並べる(SPEC §6.5)。
 * 予定は開始時刻の位置・長さで置き、時間が重なる予定は横に分ける。現在時刻に赤い線を引き、
 * 終わった予定は薄くする。終日の予定は時間軸の上の帯に出す。
 * date は日本時間の日付(YYYY-MM-DD)。now は現在時刻(テスト用に差し替えられる)。
 */
export function DayTimeline({
  date,
  events,
  calendars = [],
  now: nowProp,
}: {
  date: string;
  events: readonly CalendarEventView[];
  /** 表示中のカレンダー。予定の色(カレンダーの色)と、詳細に出すカレンダー名に使う。 */
  calendars?: readonly ShownCalendar[];
  now?: Date;
}) {
  const ticking = useNow();
  const now = nowProp ?? ticking;
  const calendarOf = (e: CalendarEventView) => calendars.find((c) => c.id === e.calendarId);
  // 複数のカレンダーを重ねて表示しているときだけ、詳細にカレンダー名を出す
  const showCalendarName = calendars.length > 1;
  // 終日の帯には、勤務場所を含めない(勤務場所は時間軸の左に印とバーで出す)
  const allDay = events.filter((e) => e.allDay && !e.workingLocation);
  const placed = placeEvents(date, events);
  const locations = placeWorkingLocations(date, events);
  const { start: startHour, end: endHour } = visibleHours([...placed, ...locations]);
  // 勤務場所がある日は、予定をバーの右から始める(Google カレンダーと同じ)
  const inset = locations.length > 0 ? WORKING_LOCATION_INSET : 0;
  const startMin = startHour * 60;
  const px = (min: number) => ((min - startMin) / 60) * HOUR_PX;
  const height = (endHour - startHour) * HOUR_PX;
  const nowMin = minutesFromDayStart(date, now.toISOString());
  // 日付が今日のときだけ、現在時刻の線を出す
  const isToday =
    new Date(`${date}T00:00:00+09:00`).getTime() <= now.getTime() &&
    now.getTime() < new Date(`${date}T00:00:00+09:00`).getTime() + 24 * 60 * 60_000;
  const showNow = isToday && nowMin >= startMin && nowMin <= endHour * 60;
  // 過去の日は、その日の予定をすべて「終わった色」にする(ユーザー要望 2026-10-07)
  const isPastDay =
    now.getTime() >= new Date(`${date}T00:00:00+09:00`).getTime() + 24 * 60 * 60_000;

  // 開いたとき、スクロール領域の上端が「現在時刻の 1 時間前」になるようにする(これから先の予定を見たいため)。
  // 今日でなければ、最初の予定の 1 時間前。表示範囲の終わりが近いときは、スクロールの下限(一番下)で止まる
  // (そのために下へ余白を足すことはしない。ユーザー判断 2026-10-07)。
  const anchor = isToday
    ? Math.min(Math.max(nowMin, startMin), endHour * 60)
    : (placed[0]?.startMin ?? startMin);
  const initialScroll = Math.max(0, px(anchor) - HOUR_PX);
  const scroller = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = initialScroll;
    // 初回だけ(予定が更新されるたびに、見ている位置を動かさない)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex flex-col gap-2 lg:min-h-0 lg:flex-1" data-testid="day-timeline">
      {allDay.length > 0 && (
        <ul aria-label="終日の予定" className="flex flex-col gap-1">
          {allDay.map((e) => (
            <AllDayChip
              key={e.id}
              event={e}
              calendar={calendarOf(e)}
              showCalendarName={showCalendarName}
            />
          ))}
        </ul>
      )}
      <div
        ref={scroller}
        data-testid="timeline-scroll"
        data-initial-scroll={initialScroll}
        // 広い画面では残りの高さいっぱいまで使う。バーはあふれたときだけ出す(強制表示しない。2026-10-07)
        className="max-h-[26rem] overflow-y-auto lg:max-h-none lg:min-h-0 lg:flex-1"
      >
        <div className="relative flex" style={{ height: height + 16, paddingTop: 8 }}>
          {/* 時間の目盛り */}
          <div aria-hidden="true" className="relative w-11 shrink-0">
            {Array.from({ length: endHour - startHour + 1 }, (_, i) => (
              <span
                key={i}
                className="absolute right-2 -translate-y-1/2 text-[11px] tabular-nums text-muted-foreground"
                style={{ top: i * HOUR_PX }}
              >
                {startHour + i}:00
              </span>
            ))}
          </div>
          <div className="relative flex-1 border-l">
            {Array.from({ length: endHour - startHour + 1 }, (_, i) => (
              <div
                key={i}
                aria-hidden="true"
                className="absolute inset-x-0 border-t border-border/70"
                style={{ top: i * HOUR_PX }}
              />
            ))}
            {locations.length > 0 && (
              <ul aria-label="勤務場所">
                {locations.map((w) => (
                  <WorkingLocationMark
                    key={w.event.id}
                    placed={w}
                    top={px(w.startMin ?? startMin)}
                    bottom={px(w.endMin ?? endHour * 60)}
                  />
                ))}
              </ul>
            )}
            <ul
              aria-label="時間のある予定"
              className="absolute inset-y-0 right-0"
              style={{ left: inset }}
            >
              {placed.map((p) => (
                <TimedEvent
                  key={p.event.id}
                  placed={p}
                  calendar={calendarOf(p.event)}
                  showCalendarName={showCalendarName}
                  top={px(p.startMin)}
                  height={px(p.endMin) - px(p.startMin)}
                  past={isPastDay || (isToday && p.endMin <= nowMin)}
                />
              ))}
            </ul>
            {showNow && (
              <div
                role="separator"
                aria-label="現在時刻"
                className="pointer-events-none absolute inset-x-0 z-20 border-t-2 border-red-500"
                style={{ top: px(nowMin) }}
              >
                <span className="absolute -top-[5px] -left-[5px] size-2 rounded-full bg-red-500" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Google Meet のアイコン(simple-icons)。白い背景のボタンには公式の色、黒いボタンには
 * mono を付けて、背景なしの単色(ボタンの文字色。白)にする。
 */
function MeetIcon({ className, mono = false }: { className?: string; mono?: boolean }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className={className}
      fill={mono ? "currentColor" : `#${siGooglemeet.hex}`}
      data-mono={mono ? "" : undefined}
    >
      <path d={siGooglemeet.path} />
    </svg>
  );
}

/** マウスを乗せてから詳細を開くまで / 離れてから閉じるまで(ms)。通り道の隙間で閉じないための猶予も兼ねる。 */
const OPEN_DELAY_MS = 120;
const CLOSE_DELAY_MS = 180;
const isMouse = (e: React.PointerEvent) => e.pointerType === "mouse" || e.pointerType === "pen";

/**
 * 予定の詳細(ポップオーバー)の開閉。**予定か詳細にマウスを乗せている間だけ開く**(ユーザー要望 2026-10-07)。
 * - マウスを予定(右上のボタンを含む予定全体)に乗せると開き、予定からも詳細からも離れると閉じる。
 *   予定と詳細の間を通るあいだに閉じてしまわないよう、閉じるまで少し待つ。
 * - クリック(タッチ・キーボードの Enter)でも開ける。この場合も、離れると閉じる。Esc・外のクリックでも閉じる。
 * - ホバーで開いたときは、フォーカスを奪わない(クリック・キーボードで開いたときは詳細へ移す)。
 * - タッチ操作の pointerenter / leave は無視する(指を離すだけで閉じてしまうため)。
 */
function useHoverDetails() {
  const [open, setOpen] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const openedBy = useRef<"hover" | "click">("click");
  const clear = () => window.clearTimeout(timer.current);
  useEffect(() => clear, []);
  const schedule = (next: boolean, delay: number) => {
    clear();
    timer.current = window.setTimeout(() => {
      if (next) openedBy.current = "hover";
      setOpen(next);
    }, delay);
  };
  // enter ではなく over(子や、ポータルで出した詳細の中の動きも伝わる)で「乗っている」を見る。
  // 詳細は React の木の上では予定の子なので、詳細から予定の右上のボタンへ移っても、予定への
  // enter は起きない。over なら、そのたびに「閉じる予約」を取り消せる
  const onPointerOver = (e: React.PointerEvent) => {
    if (isMouse(e)) schedule(true, OPEN_DELAY_MS);
  };
  const onPointerLeave = (e: React.PointerEvent) => {
    if (isMouse(e)) schedule(false, CLOSE_DELAY_MS);
  };
  return {
    open,
    /** Esc・外のクリックなど。 */
    onOpenChange: (next: boolean) => {
      clear();
      setOpen(next);
    },
    /** 予定全体と、詳細の両方に付ける。 */
    hover: { onPointerOver, onPointerLeave },
    /** クリックは「開く」(開いているのを閉じない。ホバーで開いた直後のクリックで閉じないように)。 */
    onTriggerClick: (e: React.MouseEvent) => {
      e.preventDefault(); // ライブラリの開閉の切り替えをさせない
      clear();
      openedBy.current = "click";
      setOpen(true);
    },
    onOpenAutoFocus: (e: Event) => {
      if (openedBy.current === "hover") e.preventDefault();
    },
    /**
     * ライブラリは、閉じるときに予定のボタンへフォーカスを戻す。ホバーで開いて離れて閉じた場合に
     * 戻すと、プログラムによるフォーカスなので :focus-visible になり、太いフォーカスリングが
     * 次にどこかをクリックするまで残る。ホバーで開いたときは戻さない(クリック・キーボードのときは戻す)。
     */
    onCloseAutoFocus: (e: Event) => {
      if (openedBy.current === "hover") e.preventDefault();
    },
  };
}
type HoverDetails = ReturnType<typeof useHoverDetails>;

/**
 * 予定の詳細の中身。Google カレンダーへ移動せずに、ここで見られる。
 * ポータルが持つのは、タイトル・日時・場所・Meet・Google カレンダー上の URL だけ(説明文や参加者は読まない)。
 */
function EventDetailsContent({
  event: e,
  detail,
  calendar,
  showCalendarName,
}: {
  event: CalendarEventView;
  detail: HoverDetails;
  calendar?: ShownCalendar | undefined;
  showCalendarName: boolean;
}) {
  const attachments = e.attachments;
  return (
    <PopoverContent
      align="start"
      className="w-80 text-sm"
      aria-label={`${e.title} の詳細`}
      onOpenAutoFocus={detail.onOpenAutoFocus}
      onCloseAutoFocus={detail.onCloseAutoFocus}

      {...detail.hover}
    >
      <div className="flex flex-col gap-2">
        <p className="leading-snug font-semibold break-words">{e.title}</p>
        {e.declined && (
          <p className="flex items-center gap-1.5 text-muted-foreground">
            <CircleX aria-hidden="true" className="size-4 shrink-0" />
            不参加
          </p>
        )}
        <p className="text-muted-foreground">
          {dateLabel.format(new Date(e.start))} · {e.allDay ? "終日" : range(e)}
        </p>
        {showCalendarName && calendar && (
          // どのカレンダーの予定か(カレンダーの色の印つき)
          <p className="flex items-center gap-1.5 text-muted-foreground">
            <span
              aria-hidden="true"
              className="inline-block size-2.5 shrink-0"
              style={{ backgroundColor: calendarColor(calendar) }}
            />
            <span className="break-words">{calendar.name}</span>
          </p>
        )}
        {e.location && (
          <p className="flex items-start gap-1.5">
            <MapPin aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
            <span className="break-words">{e.location}</span>
          </p>
        )}
        {attachments.length > 0 && (
          // Meet の議事録(Gemini のメモ)・文字起こし・録画は、予定の添付として付く。新しいタブで開く
          <section aria-label="議事録・添付ファイル" className="flex flex-col gap-1">
            <p className="text-xs text-muted-foreground">議事録・添付ファイル</p>
            <ul className="flex flex-col gap-0.5">
              {attachments.map((a) => {
                const Icon = attachmentIcon(a.mimeType);
                return (
                  <li key={a.url}>
                    <a
                      href={a.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={a.title}
                      className="flex items-center gap-1.5 rounded px-1 py-0.5 text-sky-800 hover:bg-sky-50 hover:underline"
                    >
                      <Icon aria-hidden="true" className="size-4 shrink-0" />
                      <span className="truncate">{a.title}</span>
                    </a>
                  </li>
                );
              })}
            </ul>
          </section>
        )}
        {(e.meetUrl || e.url) && (
          <div className="flex flex-wrap gap-2 pt-1">
            {e.meetUrl && (
              <Button asChild size="sm">
                <a href={e.meetUrl} target="_blank" rel="noopener noreferrer">
                  <MeetIcon mono className="size-4" />
                  Meet に参加
                </a>
              </Button>
            )}
            {e.url && (
              <Button asChild size="sm" variant="outline">
                <a href={e.url} target="_blank" rel="noopener noreferrer">
                  <ExternalLink />
                  Google カレンダーで開く
                </a>
              </Button>
            )}
          </div>
        )}
      </div>
    </PopoverContent>
  );
}

/** 添付ファイルのアイコン。ドキュメント(議事録・文字起こし)、動画(録画)、それ以外。 */
function attachmentIcon(mimeType: string | null) {
  if (mimeType?.startsWith("video/")) return Video;
  if (mimeType === "application/vnd.google-apps.document") return FileText;
  return Paperclip;
}

/** 終日の予定の帯。 */
function AllDayChip({
  event: e,
  calendar,
  showCalendarName,
}: {
  event: CalendarEventView;
  calendar?: ShownCalendar | undefined;
  showCalendarName: boolean;
}) {
  const color = calendarColor(calendar);
  const detail = useHoverDetails();
  return (
    <li {...detail.hover}>
      <Popover open={detail.open} onOpenChange={detail.onOpenChange}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={`${e.title}(終日)${declinedLabel(e)} の詳細を表示`}
            onClick={detail.onTriggerClick}
            data-declined={e.declined ? "true" : undefined}
            // 帯の色はカレンダーの色(不透明)。文字は、その色の明るさで白か黒を選ぶ。
            // 「参加しない」と返事した予定は、薄い色(白との混色。不透明)と取り消し線にする(2026-10-15)
            className={`block w-full truncate rounded-sm px-2.5 py-1 text-left text-xs font-medium hover:brightness-95 ${e.declined ? "border border-[color-mix(in_srgb,var(--ev)_45%,white)] bg-[color-mix(in_srgb,var(--ev)_7%,white)] text-gray-500 line-through" : ""}`}
            style={
              (e.declined
                ? { "--ev": color }
                : { backgroundColor: color, color: readableOn(color) }) as CSSProperties
            }
          >
            {e.title}
          </button>
        </PopoverTrigger>
        <EventDetailsContent
          event={e}
          detail={detail}
          calendar={calendar}
          showCalendarName={showCalendarName}
        />
      </Popover>
    </li>
  );
}

function TimedEvent({
  placed,
  calendar,
  showCalendarName,
  top,
  height,
  past,
}: {
  placed: PlacedEvent;
  calendar?: ShownCalendar | undefined;
  showCalendarName: boolean;
  top: number;
  height: number;
  past: boolean;
}) {
  const { event: e, column, columns } = placed;
  const h = Math.max(height, MIN_BLOCK_PX);
  const compact = h < 48; // 短い予定は、タイトルと時刻を 1 行にする
  // 右上のボタン(外部リンク・Meet。24px)に、タイトルが重ならないよう空ける
  const reserved = (e.url ? 28 : 0) + (e.meetUrl ? 28 : 0);
  const detail = useHoverDetails();
  // 終わった予定は、透明度(opacity)ではなく、不透明な薄い色にする。透明にすると、枠線や背景が
  // 半透明になり、後ろの時間の罫線や重なった部分が透けて見える(ユーザー報告 2026-10-07)
  // これから先の予定は、単色の塗りつぶしで枠なし(文字は色の明るさで白か黒。ユーザー要望 2026-10-07)
  // 「参加しない」と返事した予定も、終わった予定と同じ薄い色にし、タイトルに取り消し線を引く(2026-10-15)
  const faded = past || e.declined;
  const subText = faded ? "text-gray-500" : "opacity-85";
  const titleClass = e.declined ? "font-medium line-through" : "font-medium";
  // 予定の色は、カレンダーの色。薄い色は、白との混色(不透明)で作る
  const color = calendarColor(calendar);
  return (
    <li
      aria-label={`${e.title} ${range(e)}${declinedLabel(e)}`}
      // 右上のボタンを含む予定全体が、マウスを乗せる範囲
      {...detail.hover}
      data-past={past ? "true" : undefined}
      data-declined={e.declined ? "true" : undefined}
      className="absolute"
      style={{
        top,
        height: h,
        left: `calc(${(column / columns) * 100}% + 2px)`,
        width: `calc(${100 / columns}% - 4px)`,
      }}
    >
      {/* 予定全体がボタン。マウスを乗せている間、または押すと、詳細(ポップオーバー)を開く。
          右上のボタンは、このボタンの外に重ねて置く */}
      <Popover open={detail.open} onOpenChange={detail.onOpenChange}>
        <PopoverTrigger asChild>
          <button
            type="button"
            onClick={detail.onTriggerClick}
            aria-label={`${e.title} ${range(e)}${declinedLabel(e)} の詳細を表示`}
            className={`flex h-full w-full flex-col justify-center overflow-hidden rounded-md border py-1 pl-2.5 text-left text-xs leading-tight focus-visible:ring-2 focus-visible:ring-[var(--ev)] focus-visible:outline-none ${faded ? "border-[color-mix(in_srgb,var(--ev)_45%,white)] bg-[color-mix(in_srgb,var(--ev)_7%,white)] text-gray-500 hover:bg-[color-mix(in_srgb,var(--ev)_14%,white)]" : "border-white bg-[var(--ev)] text-[var(--ev-fg)] hover:brightness-95"}`}
            style={
              {
                paddingRight: reserved > 0 ? reserved + 10 : 10,
                "--ev": color,
                "--ev-fg": readableOn(color),
              } as CSSProperties
            }
          >
            {compact ? (
              <span className="truncate">
                <span className={titleClass}>{e.title}</span>
                <span className={subText}>, {time.format(new Date(e.start))}</span>
              </span>
            ) : (
              <>
                <span className={`truncate ${titleClass}`}>{e.title}</span>
                <span className={`truncate ${subText}`}>{range(e)}</span>
                {h >= 72 && e.location && (
                  <span className={`truncate ${subText}`}>{e.location}</span>
                )}
              </>
            )}
          </button>
        </PopoverTrigger>
        <EventDetailsContent
          event={e}
          detail={detail}
          calendar={calendar}
          showCalendarName={showCalendarName}
        />
      </Popover>
      {reserved > 0 && (
        <span
          className={`absolute right-1 z-10 flex items-center gap-1 ${compact ? "top-1/2 -translate-y-1/2" : "top-1"}`}
        >
          {e.url && (
            // Google カレンダー上の予定を新しいタブで開く(外部リンク)
            <a
              href={e.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${e.title} を Google カレンダーで開く`}
              title="Google カレンダーで開く"
              className="grid size-6 place-items-center rounded border border-sky-300 bg-white text-sky-900 hover:bg-sky-100"
            >
              <ExternalLink aria-hidden="true" className="size-3.5" />
            </a>
          )}
          {e.meetUrl && (
            // すぐ Meet に入れる。アイコンだけ(名前はスクリーンリーダー用)
            <a
              href={e.meetUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${e.title} の Meet に参加`}
              title="Meet に参加"
              className="grid size-6 place-items-center rounded border border-sky-300 bg-white hover:bg-sky-100"
            >
              <MeetIcon className="size-4" />
            </a>
          )}
        </span>
      )}
    </li>
  );
}

const LOCATION_ICONS = { OFFICE: Building2, HOME: House, CUSTOM: MapPin } as const;

/**
 * 勤務場所。時間帯の開始に「アイコンの丸 + 場所の名前」を置き、その時間帯に沿って左端に縦長のバーを引く
 * (Google カレンダーの勤務場所と同じ見た目)。予定の邪魔をしないよう、クリックは通す。
 */
function WorkingLocationMark({
  placed,
  top,
  bottom,
}: {
  placed: PlacedWorkingLocation;
  top: number;
  bottom: number;
}) {
  const location = placed.event.workingLocation;
  if (!location) return null;
  const Icon = LOCATION_ICONS[location.kind];
  const label =
    placed.startMin === null
      ? `勤務場所: ${location.label}`
      : `勤務場所: ${location.label} ${range(placed.event)}`;
  return (
    <li
      aria-label={label}
      className="pointer-events-none absolute inset-x-0 z-10 h-[22px]"
      style={{ top }}
    >
      <span
        data-testid="working-location-bar"
        className="absolute left-[5px] w-1.5 rounded-md bg-slate-300"
        style={{ top: 4, height: Math.max(0, bottom - top - 6) }}
      />
      <span className="absolute top-[2px] left-[2px] grid size-4 place-items-center rounded-full bg-slate-300 text-slate-800">
        <Icon aria-hidden="true" className="size-2.5" />
      </span>
      <span className="absolute top-[3px] left-6 text-xs leading-none">{location.label}</span>
    </li>
  );
}
